import type { Ref } from 'react';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../theme/useTheme';
import { useAuth } from '../../features/auth/useAuth';
import { useToast } from '../ui/Toast';
import BrandMark from '../ui/BrandMark';
import { usePushToggle } from './usePushToggle';
import styles from './TopBar.module.css';

type TopBarProps = {
  onMenuToggle: () => void;
  sidebarOpen: boolean;
  menuButtonRef?: Ref<HTMLButtonElement>;
};

function SunIcon() {
  return (
    <svg aria-hidden="true" focusable="false" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="5" />
      <line x1="12" y1="1" x2="12" y2="3" />
      <line x1="12" y1="21" x2="12" y2="23" />
      <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
      <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
      <line x1="1" y1="12" x2="3" y2="12" />
      <line x1="21" y1="12" x2="23" y2="12" />
      <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
      <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
    </svg>
  );
}

function MoonIcon() {
  return (
    <svg aria-hidden="true" focusable="false" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z" />
    </svg>
  );
}

function BellIcon({ active }: { active: boolean }) {
  return (
    <svg aria-hidden="true" focusable="false" width="20" height="20" viewBox="0 0 24 24" fill={active ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 8a6 6 0 10-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
      <path d="M13.73 21a2 2 0 01-3.46 0" />
    </svg>
  );
}

function HamburgerIcon({ open }: { open: boolean }) {
  return (
    <svg aria-hidden="true" focusable="false" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round">
      {open ? (
        <>
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </>
      ) : (
        <>
          <line x1="3" y1="6" x2="21" y2="6" />
          <line x1="3" y1="12" x2="21" y2="12" />
          <line x1="3" y1="18" x2="21" y2="18" />
        </>
      )}
    </svg>
  );
}

export default function TopBar({ onMenuToggle, sidebarOpen, menuButtonRef }: TopBarProps) {
  const { theme, toggle } = useTheme();
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const push = usePushToggle();

  useEffect(() => {
    if (push.error) showToast(push.error, 'error');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [push.error]);

  // enable()/disable() resolve to success/failure directly — toasting off
  // that return value (rather than the hook's state) avoids a stale read of
  // push.subscribed from this closure.
  async function handlePushToggle() {
    const wasSubscribed = push.subscribed;
    const ok = wasSubscribed ? await push.disable() : await push.enable();
    if (ok) showToast(wasSubscribed ? 'Notifications disabled.' : 'Notifications enabled.', 'success');
  }

  return (
    <header className={styles.topbar} role="banner">
      <button
        ref={menuButtonRef}
        type="button"
        className={styles.menuButton}
        onClick={onMenuToggle}
        aria-label={sidebarOpen ? 'Close navigation menu' : 'Open navigation menu'}
        aria-expanded={sidebarOpen}
        aria-controls="app-sidebar"
      >
        <HamburgerIcon open={sidebarOpen} />
      </button>

      <div className={styles.brand} aria-label="Onboarding Portal">
        <span className={styles.brandMark} aria-hidden="true">
          <BrandMark />
        </span>
        <span className={styles.brandText}>Onboarding</span>
      </div>

      <div className={styles.spacer} />

      <div className={styles.controls}>
        {push.supported && (
          <button
            type="button"
            className={styles.iconButton}
            onClick={() => void handlePushToggle()}
            disabled={push.status === 'busy'}
            aria-label={push.subscribed ? 'Disable notifications on this device' : 'Enable notifications on this device'}
            aria-pressed={push.subscribed}
          >
            <BellIcon active={push.subscribed} />
          </button>
        )}
        <button
          type="button"
          className={styles.iconButton}
          onClick={toggle}
          aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
          aria-pressed={theme === 'dark'}
        >
          {theme === 'dark' ? <SunIcon /> : <MoonIcon />}
        </button>

        {user && (
          <div className={styles.userChip}>
            <span className={styles.userChipName} aria-label={`Signed in as ${user.name}`}>
              {user.name}
              {user.roles[0] && <span className={styles.userChipRole}>{user.roles[0]}</span>}
            </span>
            <button
              type="button"
              className={styles.signOutBtn}
              onClick={() => {
                void signOut().then(() => navigate('/login', { replace: true }));
              }}
              aria-label="Sign out"
            >
              Sign out
            </button>
          </div>
        )}
      </div>
    </header>
  );
}
